import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './context/AuthContext'
import { MainLayout } from './components/layout/MainLayout'
import { LoginPage } from './pages/LoginPage'
import { CallbackPage } from './pages/CallbackPage'
import { DashboardPage } from './pages/DashboardPage'
import { TasksPage } from './pages/TasksPage'
import { TaskDetailPage } from './pages/TaskDetailPage'
import { TesterQueuePage } from './pages/TesterQueuePage'
import { KanbanPage } from './pages/KanbanPage'
import { ManagePage } from './pages/ManagePage'
import { ReleasesPage } from './pages/ReleasesPage'
import { QuoteQueuePage } from './pages/QuoteQueuePage'
import { FinanceApprovalPage } from './pages/FinanceApprovalPage'
import { LoadingSpinner } from './components/common/LoadingSpinner'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
    },
  },
})

// App content with routing
const AppContent: React.FC = () => {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-300 dark:bg-slate-950">
        <LoadingSpinner size="lg" text="Loading iPulse Systems DoD..." />
      </div>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <MainLayout>
      <Routes>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/:uniqueTaskId" element={<TaskDetailPage />} />
        <Route path="/testing" element={<TesterQueuePage />} />
        <Route path="/kanban" element={<KanbanPage />} />
        <Route path="/releases" element={<ReleasesPage />} />
        <Route path="/quotes" element={<QuoteQueuePage />} />
        <Route path="/finance-approval" element={<FinanceApprovalPage />} />
        <Route path="/manage" element={<ManagePage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </MainLayout>
  )
}

// Main App component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
